import { HashMap } from '../constants';
import { getNextNotificationUid, Notification, NotificationType } from '../models/notification';
import { Action, ActionMeta } from './commonActions';

import {
  ADD_NOTIFICATION,
  CLOSE_NOTIFICATION_PANEL,
  DISMISS_ALL_NOTIFICATIONS,
  DISMISS_NOTIFICATION,
  HIDE_ALL_NOTIFICATION_POPUPS,
  HIDE_NOTIFICATION_POPUP,
  OPEN_NOTIFICATION_PANEL,
} from './actionTypes';

/**
 * Object which encapsulates a clientside notification.
 */
export interface NewNotification {
  /**
   * The high-level type of the notification.
   */
  type: NotificationType;

  /**
   * The notification category.
   */
  category?: string;

  /**
   * The main title of the notification.
   */
  title: string;

  /**
   * Additional detail about the notification.
   */
  detail?: string;

  /**
   * Additional notification-specific data.
   */
  additionalData?: HashMap<any>;

  /**
   * Determines whether or not the notification should show in the menu count.
   */
  showInCount?: boolean;

  /**
   * An optional action which can be triggered from the notification.
   */
  action?: () => void;

  /**
   * The label to use for the action button.
   */
  actionLabel?: string;
}

/**
 * Adds a new notification to the notification list.
 * @param notification The notification to add.
 */
export function addNotification(notification: NewNotification): Action<Notification> {
  const instance = Object.assign({}, notification, {
    uid: getNextNotificationUid(),
    raisedOn: new Date(),
    read: !notification.showInCount,
    hidden: false,
  });

  return {
    type: ADD_NOTIFICATION,
    payload: instance,
  };
}

/**
 * Marks the popup notification as hidden.
 * @param uid The unique identifier of a notification.
 */
export function hideNotificationPopup(uid: number): Action<number> {
  return {
    type: HIDE_NOTIFICATION_POPUP,
    payload: uid,
  };
}

/**
 * Action which is dispatched to close the notification panel.
 */
export function closeNotificationPanel(): Action<{}> {
  return {
    type: CLOSE_NOTIFICATION_PANEL,
  };
}

/**
 * Action which is dispatched to open the notification panel.
 */
export function openNotificationPanel(): Action<{}> {
  return {
    type: OPEN_NOTIFICATION_PANEL,
  };
}

/**
 * Action which is dispatched to remove all notification popups.
 */
export function hideAllNotificationPopups(): Action<{}> {
  return {
    type: HIDE_ALL_NOTIFICATION_POPUPS,
  };
}

/**
 * Action which dismisses all notifications.
 * @param type Optional. If specified, limits the notifications affected to those with the specified type.
 */
export function dismissAllNotifications(type?: NotificationType): Action<NotificationType> {
  return {
    type: DISMISS_ALL_NOTIFICATIONS,
    payload: type,
  };
}

/**
 * Action which dismisses a notification.
 * @param id The identifier of a notification.
 */
export function dismissNotification(id: number): Action<number> {
  return {
    type: DISMISS_NOTIFICATION,
    payload: id,
  };
}
